<template>
  <!-- ECOUNT ERP 관리 (SPEC-ECOUNT-ETL-CRON-CONSOLE-002 Phase 3) -->
  <!-- 딜러 CronConsole 패턴 답습 + ECOUNT 특화 (cron 2종: GTIN / Serial-Lot) -->
  <!-- UI 개편: #1 마스터 토글 manufacturer 편집 허용 / #3 동기화 현황 통합 / #4 섹션 재배치 -->
  <div class="page-container-v2">
    <div class="page-header">
      <span class="page-title">{{ $t('ecountErpConsole_title') }}</span>
      <a-button size="small" :loading="loading" style="margin-left: 12px;" @click="fetchAll">
        {{ $t('ecountErpConsole_refresh') }}
      </a-button>
    </div>

    <a-alert v-if="errorMsg" type="error" :message="errorMsg" style="margin-bottom: 16px;" show-icon closable @close="errorMsg = ''" />
    <a-alert v-if="successMsg" type="success" :message="successMsg" style="margin-bottom: 16px;" show-icon closable @close="successMsg = ''" />

    <!-- ① 마스터 스위치 2종 (GTIN / Serial-Lot) -->
    <!-- #1: !config.masterEnabled 조건 제거 → manufacturer도 즉시 편집 가능 -->
    <a-card :title="$t('ecountErpConsole_master_title')" style="margin-bottom: 16px;" :loading="loading">
      <div class="master-row">
        <div class="master-label">
          <a-tag color="blue">{{ $t('ecountErpConsole_job_gtin') }}</a-tag>
          <span class="version-label" v-if="config">v{{ config.version }}</span>
        </div>
        <a-switch
          :checked="config && config.gtinSyncEnabled"
          :disabled="!canEdit || !config || saving"
          :loading="saving === 'gtin'"
          :aria-label="$t('ecountErpConsole_job_gtin')"
          @change="onToggleGtin"
        />
      </div>
      <div class="master-row">
        <div class="master-label">
          <a-tag color="cyan">{{ $t('ecountErpConsole_job_serial_lot') }}</a-tag>
        </div>
        <a-switch
          :checked="config && config.serialLotSyncEnabled"
          :disabled="!canEdit || !config || saving"
          :loading="saving === 'serial-lot'"
          :aria-label="$t('ecountErpConsole_job_serial_lot')"
          @change="onToggleSerialLot"
        />
      </div>
      <div v-if="!canEdit" class="permission-warning">
        <a-tag color="orange">{{ $t('ecountErpConsole_certilife_only') }}</a-tag>
      </div>
    </a-card>

    <!-- ② 데이터 동기화 주기 (시 단위 입력 위젯) -->
    <a-card :title="$t('ecountErpConsole_cron_title')" style="margin-bottom: 16px;" :loading="loading">
      <div v-if="config" class="cron-edit-row">
        <div class="cron-label">
          <a-tag color="blue">{{ $t('ecountErpConsole_job_gtin') }}</a-tag>
        </div>
        <div class="cron-edit-body">
          <div class="cron-current">
            <code>{{ config.gtinCronExpr || '-' }}</code>
            <span class="cron-translated">{{ cronToLabel(config.gtinCronExpr) }}</span>
          </div>
          <div class="cron-next">
            {{ $t('ecountErpConsole_next') }}: {{ computedNextGtinAt }}
          </div>
          <div v-if="canEdit" class="cron-hour-row">
            <span class="cron-hour-prefix">0</span>
            <a-input-number
              v-model:value="cronEdit.gtinHour"
              :min="0"
              :max="23"
              :precision="0"
              :disabled="cronSaving"
              style="width: 70px;"
            />
            <span class="cron-hour-suffix">* * *</span>
            <span class="hint">{{ $t('ecountErpConsole_cron_help') }}</span>
          </div>
        </div>
      </div>
      <div v-if="config" class="cron-edit-row">
        <div class="cron-label">
          <a-tag color="cyan">{{ $t('ecountErpConsole_job_serial_lot') }}</a-tag>
        </div>
        <div class="cron-edit-body">
          <div class="cron-current">
            <code>{{ config.serialLotCronExpr || '-' }}</code>
            <span class="cron-translated">{{ cronToLabel(config.serialLotCronExpr) }}</span>
          </div>
          <div class="cron-next">
            {{ $t('ecountErpConsole_next') }}: {{ computedNextSerialLotAt }}
          </div>
          <div v-if="canEdit" class="cron-hour-row">
            <span class="cron-hour-prefix">0</span>
            <a-input-number
              v-model:value="cronEdit.serialLotHour"
              :min="0"
              :max="23"
              :precision="0"
              :disabled="cronSaving"
              style="width: 70px;"
            />
            <span class="cron-hour-suffix">* * *</span>
            <span class="hint">{{ $t('ecountErpConsole_cron_help') }}</span>
          </div>
        </div>
      </div>
      <div v-if="canEdit && config" style="margin-top: 14px; display: flex; align-items: center; gap: 12px; flex-wrap: wrap;">
        <a-button
          type="primary"
          size="small"
          :loading="cronSaving"
          :disabled="!config || cronSaving"
          @click="onApplyCron"
        >
          {{ $t('ecountErpConsole_cron_apply') }}
        </a-button>
      </div>
    </a-card>

    <!-- ③ ECAPI 세션 상태 (#4 재배치: 수동 재실행 앞으로 이동) -->
    <session-panel
      :session="obsSession"
      :loading="obsLoading.session"
      :error="obsErrors.session"
      :is-super-admin="isSuperAdmin"
      @session-updated="onSessionUpdated"
      @refresh="obsRefreshAll"
    />

    <!-- ④ 수동 재실행 -->
    <a-card :title="$t('ecountErpConsole_trigger_title')" style="margin-bottom: 16px;" :loading="loading">
      <div v-if="!canEdit" class="permission-warning" style="margin-bottom: 12px;">
        <a-tag color="orange">{{ $t('ecountErpConsole_certilife_only') }}</a-tag>
      </div>
      <div class="trigger-row">
        <a-button
          :loading="triggering.gtin"
          :disabled="!canEdit || triggering.gtin || triggering.serialLot"
          @click="onTriggerGtin"
        >
          {{ $t('ecountErpConsole_trigger_gtin') }}
        </a-button>
        <!-- 시리얼/로트 동기화는 ECAPI 세션이 활성(ACTIVE/EXPIRING_SOON)일 때만 실행 가능 -->
        <a-tooltip :title="serialLotTriggerTooltip">
          <!-- disabled 버튼은 hover 이벤트가 죽어 tooltip이 안 뜨므로 span으로 감싸 hover 수신 -->
          <span class="trigger-btn-wrap">
            <a-button
              :loading="triggering.serialLot"
              :disabled="!canEdit || triggering.gtin || triggering.serialLot || !ecapiSessionReady"
              @click="onTriggerSerialLot"
            >
              {{ $t('ecountErpConsole_trigger_serial_lot') }}
            </a-button>
          </span>
        </a-tooltip>
      </div>
      <!-- 시리얼/로트 동기화 ECAPI 세션 의존 상태 표기 -->
      <div class="trigger-session-hint">
        <a-tag :color="ecapiSessionReady ? 'green' : 'default'">
          {{ ecapiSessionReady ? $t('ecountErpConsole_session_ready_tag') : $t('ecountErpConsole_session_blocked_tag') }}
        </a-tag>
        <span class="hint">{{ $t('ecountErpConsole_trigger_serial_lot_dep_note') }}</span>
      </div>
      <div v-if="lastTrigger.runId" class="trigger-result">
        <a-tag :color="lastTrigger.status === 'success' ? 'green' : 'red'">{{ lastTrigger.status }}</a-tag>
        <span class="hint" style="margin-left: 8px;">
          runId: {{ lastTrigger.runId }}
        </span>
      </div>
    </a-card>

    <!-- ⑤ 동기화 현황 (통합 섹션: 기존 last-sync-card + OapiQuotaBadge + BatchHealthPanel 대체) -->
    <!-- ⑥ 변경 이력 (딜러 CronConsole 패턴 미러링 — canEdit 권한 가드) -->
    <!-- #3: config.lastGtinSyncAt/lastSerialLotSyncAt 대신 obsHealth.lastGtinRun/lastSerialLotRun 사용 -->
    <a-card :title="$t('ecountErpConsole_sync_status_title')" style="margin-bottom: 16px;">
      <!-- batch unreachable 경고 -->
      <a-alert
        v-if="obsErrors.health && obsErrors.health.type === 'unreachable'"
        type="error"
        :message="$t('ecountEtl_obs_unreachable')"
        style="margin-bottom: 12px;"
        show-icon
      />
      <!-- 일반 health 에러 -->
      <a-alert
        v-else-if="obsErrors.health"
        type="error"
        :message="obsErrors.health.message || $t('ecountEtl_obs_error_unknown')"
        style="margin-bottom: 12px;"
        show-icon
      />

      <!-- 로딩 -->
      <div v-if="obsLoading.health || obsLoading.quota" style="text-align: center; padding: 16px;">
        <a-spin />
      </div>

      <template v-else>
        <!-- 마지막 실행 — obsHealth 기반 (신뢰 가능) -->
        <a-descriptions
          size="small"
          :column="{ xs: 1, sm: 2 }"
          bordered
          style="margin-bottom: 16px;"
        >
          <a-descriptions-item :label="$t('ecountEtl_obs_health_lastGtinRun')">
            <template v-if="obsHealth && obsHealth.lastGtinRun">
              <a-tag :color="runStatusColor(obsHealth.lastGtinRun.status)" style="margin-right: 4px;">
                {{ obsHealth.lastGtinRun.status }}
              </a-tag>
              {{ formatObsDateTime(obsHealth.lastGtinRun.at) }}
              <span v-if="obsHealth.lastGtinRun.fetchedRows != null" class="hint" style="margin-left: 6px;">
                fetched {{ obsHealth.lastGtinRun.fetchedRows }}
                <template v-if="obsHealth.lastGtinRun.inserted != null || obsHealth.lastGtinRun.updated != null">
                  · 적재 {{ (obsHealth.lastGtinRun.inserted || 0) + (obsHealth.lastGtinRun.updated || 0) }}
                </template>
              </span>
            </template>
            <span v-else>-</span>
          </a-descriptions-item>
          <a-descriptions-item :label="$t('ecountEtl_obs_health_lastSerialLotRun')">
            <template v-if="obsHealth && obsHealth.lastSerialLotRun">
              <a-tag :color="runStatusColor(obsHealth.lastSerialLotRun.status)" style="margin-right: 4px;">
                {{ obsHealth.lastSerialLotRun.status }}
              </a-tag>
              {{ formatObsDateTime(obsHealth.lastSerialLotRun.at) }}
              <span v-if="obsHealth.lastSerialLotRun.fetchedRows != null" class="hint" style="margin-left: 6px;">
                fetched {{ obsHealth.lastSerialLotRun.fetchedRows }}
                <template v-if="obsHealth.lastSerialLotRun.inserted != null || obsHealth.lastSerialLotRun.updated != null">
                  · 적재 {{ (obsHealth.lastSerialLotRun.inserted || 0) + (obsHealth.lastSerialLotRun.updated || 0) }}
                </template>
              </span>
            </template>
            <span v-else>-</span>
          </a-descriptions-item>
        </a-descriptions>

        <!-- OAPI 키 안내 (호출 수 필드는 제거, 키 안내만 유지) -->
        <div v-if="obsQuota" style="margin-bottom: 16px;">
          <a-alert
            type="info"
            :message="$t('ecountEtl_obs_quota_key_note')"
            show-icon
          />
        </div>

        <!-- 7일 EtlRun 분포 (obsHealth.etlRuns7d) -->
        <div v-if="obsHealth && obsHealth.etlRuns7d" style="margin-bottom: 8px;">
          <div style="margin-bottom: 8px; font-weight: 500;">{{ $t('ecountEtl_obs_health_etlRuns7d') }}</div>
          <a-row :gutter="8">
            <a-col v-for="item in syncEtlRuns7dList" :key="item.status" :xs="12" :sm="6">
              <a-statistic
                :title="item.label"
                :value="item.count"
                :value-style="item.valueStyle"
                style="text-align: center;"
              />
            </a-col>
          </a-row>
          <div style="margin-top: 12px;">
            <span style="font-size: 12px; color: rgba(0,0,0,0.45);">
              {{ $t('ecountEtl_obs_health_successRate') }}: {{ syncSuccessRate.toFixed(1) }}%
            </span>
            <a-progress
              :percent="syncSuccessRate"
              :stroke-color="syncSuccessRate >= 80 ? '#52c41a' : '#fa8c16'"
              :status="syncSuccessRate < 80 ? 'exception' : 'normal'"
              size="small"
            />
          </div>
        </div>

        <!-- 데이터 없음 -->
        <a-empty v-if="!obsHealth && !obsQuota && !obsErrors.health && !obsErrors.quota" :description="$t('ecountEtl_obs_no_data')" />
      </template>
    </a-card>
    <!-- ⑥ 변경 이력 카드 (canEdit = 편집 권한자에게만 노출) -->
    <a-card v-if="canEdit" :title="$t('ecountErpConsole_audits_title')" :loading="loadingAudits" style="margin-bottom: 16px;">
      <a-table
        :data-source="audits"
        :columns="auditColumns"
        :pagination="false"
        row-key="_id"
        size="small"
        :scroll="{ x: 950 }"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'changedAt'">
            {{ formatObsDateTime(record.changedAt) }}
          </template>
          <template v-else-if="column.key === 'version'">
            v{{ record.fromVersion }} → v{{ record.toVersion }}
          </template>
          <template v-else-if="column.key === 'changedBy'">
            {{ record.changedBy || '-' }}
          </template>
          <template v-else-if="column.key === 'change'">
            <div v-for="(d, i) in diffAuditChanges(record)" :key="i" class="audit-diff-row">
              <span class="diff-field">{{ d.field }}:</span>
              <span class="diff-before">{{ d.before }}</span>
              <span class="diff-arrow">→</span>
              <span class="diff-after">{{ d.after }}</span>
            </div>
            <span v-if="diffAuditChanges(record).length === 0">-</span>
          </template>
          <template v-else-if="column.key === 'action'">
            <a-button
              size="small"
              :disabled="isLatestAudit(record) || !!saving || restoring === record._id"
              :loading="restoring === record._id"
              @click="onRestoreClick(record)"
            >
              {{ $t('ecountErpConsole_audits_restore') }}
            </a-button>
          </template>
        </template>
      </a-table>
    </a-card>

    <!-- 복원 확인 모달 -->
    <a-modal
      :open="restoreModal.open"
      :title="$t('ecountErpConsole_audits_restore_confirm_title')"
      :ok-text="$t('ecountErpConsole_audits_restore_confirm_ok')"
      :ok-button-props="{ danger: true }"
      @ok="onConfirmRestore"
      @cancel="restoreModal.open = false"
    >
      <p>{{ $t('ecountErpConsole_audits_restore_confirm_msg', { v: restoreModal.target ? restoreModal.target.toVersion : '-' }) }}</p>
      <div v-if="restoreModal.target" style="margin-top: 12px; padding: 8px; background: #fafafa; border-radius: 4px;">
        <div v-for="(d, i) in diffAuditChanges(restoreModal.target)" :key="i" class="audit-diff-row" style="font-size: 12px;">
          <span class="diff-field">{{ d.field }}:</span>
          <span class="diff-after">{{ d.after }}</span>
          <span class="diff-arrow">(복원 대상)</span>
        </div>
      </div>
    </a-modal>

    <!-- 변경 사유 입력 모달 -->
    <a-modal
      :open="reasonModal.open"
      :title="$t('ecountErpConsole_audits_reason_title')"
      @ok="onConfirmReason"
      @cancel="onCancelReason"
    >
      <a-input v-model:value="reasonModal.reason" :placeholder="$t('ecountErpConsole_audits_reason_ph')" />
    </a-modal>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import ecountEtlApi, { extractApiError } from '@/apis/ecountEtl';
import dealerEcountListMixin from '@/mixins/dealerEcountListMixin';
import { useObservability } from './composables/useObservability.js';
import SessionPanel from './components/SessionPanel.vue';

// @MX:ANCHOR: [AUTO] SPEC-ECOUNT-ETL-CRON-CONSOLE-002 Phase 3 + UI 개편 (#1/#3/#4)
// @MX:REASON: [AUTO] ECOUNT ERP 운영 콘솔 단일 진입점 — 마스터 토글 + 수동 재실행 + 동기화 현황 통합. fan_in >= 3 (라우터 + 사이드바 + 관측성 composable)

export default {
  name: 'EcountErpConsole',
  mixins: [dealerEcountListMixin],
  components: {
    SessionPanel,
  },

  // @MX:NOTE: [AUTO] Options API + setup() 혼용 패턴 — Dashboard.vue 검증 패턴 답습 (A-3)
  // setup()은 useObservability composable 호출 전용. 기존 data/methods/computed(마스터 토글/cron/수동 재실행)는 보존.
  setup() {
    // @MX:NOTE: [AUTO] 중국 admin 권한 모델 — 운영자 가드 미사용, 메뉴 노출/비노출로 통제.
    // ECOUNT ETL 메뉴가 보이는(privileges 노출) 계정은 패널 + reauth/invalidate 버튼을 모두 사용.
    // 따라서 isSuperAdmin은 메뉴 진입 = 사용 권한으로 항상 true (BE는 XINLAIAN+MANUFACTURER 허용).
    const isSuperAdmin = ref(true);

    // REQ-INT-003/004: useObservability 30초 polling + onUnmounted clearInterval 자동 등록
    // onMounted에서 최초 fetch, onUnmounted에서 clearInterval → REQ-POLL-002/003 polling 누수 0 보장
    const {
      session: obsSession,
      quota: obsQuota,
      health: obsHealth,
      loading: obsLoading,
      errors: obsErrors,
      fetchAll: obsRefreshAll,
      fetchSession: obsRefreshSession,
    } = useObservability({ intervalMs: 30000 });

    // #3 동기화 현황 통합 섹션용 — 7일 EtlRun 분포 computed
    const syncEtlRuns7dList = computed(() => {
      const runs = obsHealth.value?.etlRuns7d;
      if (!runs) return [];
      return [
        { status: 'success', label: '성공', count: runs.success ?? 0, valueStyle: { color: '#52c41a' } },
        { status: 'skipped', label: '스킵',  count: runs.skipped ?? 0, valueStyle: {} },
        { status: 'partial', label: '부분',  count: runs.partial ?? 0, valueStyle: { color: '#fa8c16' } },
        { status: 'failed',  label: '실패',  count: runs.failed  ?? 0, valueStyle: { color: '#f5222d' } },
      ];
    });

    // #3 동기화 현황 통합 섹션용 — 성공률 계산 (80% 기준)
    const syncSuccessRate = computed(() => {
      const runs = obsHealth.value?.etlRuns7d;
      if (!runs) return 0;
      const total = (runs.success ?? 0) + (runs.skipped ?? 0) + (runs.partial ?? 0) + (runs.failed ?? 0);
      if (total === 0) return 0;
      return parseFloat(((runs.success ?? 0) / total * 100).toFixed(1));
    });

    return {
      isSuperAdmin,
      obsSession,
      obsQuota,
      obsHealth,
      obsLoading,
      obsErrors,
      obsRefreshAll,
      obsRefreshSession,
      syncEtlRuns7dList,
      syncSuccessRate,
    };
  },

  data() {
    return {
      loading: false,
      saving: null, // 'gtin' | 'serial-lot' | null
      errorMsg: '',
      successMsg: '',

      config: null,
      // {dealerCode, masterEnabled, gtinSyncEnabled, serialLotSyncEnabled,
      //  gtinCronExpr, serialLotCronExpr, lastGtinSyncAt, lastSerialLotSyncAt,
      //  nextGtinSyncAt, nextSerialLotSyncAt, version, ...}

      triggering: { gtin: false, serialLot: false },
      lastTrigger: { runId: null, status: null },

      // Cron 편집 상태 — 시(hour) 단위 입력
      cronEdit: { gtinHour: 3, serialLotHour: 4 },
      cronSaving: false,

      // 변경 이력 + 복원 (딜러 CronConsole 패턴 미러링)
      audits: [],
      loadingAudits: false,
      restoring: null, // 복원 중인 audit._id
      restoreModal: { open: false, target: null },
      reasonModal: { open: false, reason: '', resolve: null },
    };
  },

  computed: {
    // 중국 admin: 운영자 가드 미사용. 메뉴 노출(privileges)이 곧 편집 권한.
    // 메뉴가 보이는 계정은 토글/cron/수동 재실행 모두 가능 (BE: XINLAIAN+MANUFACTURER 허용).
    canEdit() { return true; },

    // #5 다음 실행 실시간 계산 — 입력 시(hour, KST 기준) 변경 시마다 즉시 갱신
    computedNextGtinAt() {
      return this.calcNextRunAt(this.cronEdit.gtinHour);
    },
    computedNextSerialLotAt() {
      return this.calcNextRunAt(this.cronEdit.serialLotHour);
    },

    // 변경 이력 테이블 컬럼 (딜러 패턴 미러링)
    auditColumns() {
      return [
        { title: this.$t('ecountErpConsole_audits_col_changedAt'), key: 'changedAt', width: 180 },
        { title: this.$t('ecountErpConsole_audits_col_version'),   key: 'version',   width: 120 },
        { title: this.$t('ecountErpConsole_audits_col_changedBy'), key: 'changedBy', width: 140 },
        { title: this.$t('ecountErpConsole_audits_col_change'),    key: 'change' },
        { title: this.$t('ecountErpConsole_audits_col_reason'),    dataIndex: 'reason', key: 'reason', width: 200 },
        { title: this.$t('ecountErpConsole_audits_col_action'),    key: 'action', width: 90 },
      ];
    },

    // 시리얼/로트(ECAPI) 수동 동기화 가용 여부 — ECAPI 세션이 ACTIVE 또는 EXPIRING_SOON(토큰 유효)일 때만 true.
    // EXPIRED/NONE 상태(또는 세션 미조회 시 null)에서는 false → 버튼 비활성 + tooltip 안내.
    ecapiSessionReady() {
      const state = this.obsSession?.state;
      return state === 'ACTIVE' || state === 'EXPIRING_SOON';
    },

    // 시리얼/로트 동기화 버튼 tooltip — 권한/세션 상태에 따라 안내 문구 분기
    serialLotTriggerTooltip() {
      if (!this.canEdit) return this.$t('ecountErpConsole_certilife_only');
      return this.ecapiSessionReady
        ? this.$t('ecountErpConsole_trigger_serial_lot_ready')
        : this.$t('ecountErpConsole_trigger_serial_lot_blocked');
    },
  },

  mounted() {
    this.fetchAll();
    if (this.canEdit) {
      this.fetchAudits();
    }
  },

  methods: {
    async fetchAll() {
      this.loading = true;
      this.errorMsg = '';
      try {
        const res = await ecountEtlApi.getConfig();
        this.config = res.data || res;
        this.prefillCronEdit();
      } catch (err) {
        this.errorMsg = this.formatError(err);
      } finally {
        this.loading = false;
      }
    },

    prefillCronEdit() {
      if (!this.config) return;
      // "0 H * * *" 형태에서 H(시) 파싱. 파싱 실패 시 기본값 유지.
      this.cronEdit.gtinHour = this.parseHourFromCron(this.config.gtinCronExpr, 3);
      this.cronEdit.serialLotHour = this.parseHourFromCron(this.config.serialLotCronExpr, 4);
    },

    /**
     * "0 H * * *" 표현식에서 시(H) 파싱.
     * @param {string} expr
     * @param {number} fallback
     * @returns {number}
     */
    parseHourFromCron(expr, fallback) {
      if (!expr) return fallback;
      const parts = expr.trim().split(/\s+/);
      if (parts.length >= 2) {
        const h = parseInt(parts[1], 10);
        if (!isNaN(h) && h >= 0 && h <= 23) return h;
      }
      return fallback;
    },

    /**
     * #5 다음 실행 KST 시각 클라이언트 계산.
     * 입력 시(hour, 0~23, KST)에 대해: 오늘 KST H:00이 아직 안 지났으면 오늘, 지났으면 내일.
     * @param {number|null} hour
     * @returns {string}
     */
    calcNextRunAt(hour) {
      if (hour == null || isNaN(hour)) return '-';
      const h = Math.max(0, Math.min(23, Math.floor(hour)));

      // 현재 KST 시각 구하기 (UTC+9)
      const nowUtc = Date.now();
      const kstOffset = 9 * 60 * 60 * 1000;
      const nowKst = new Date(nowUtc + kstOffset);

      // 오늘 KST 기준 H:00:00 UTC timestamp
      const todayKstMidnightUtc = Date.UTC(
        nowKst.getUTCFullYear(),
        nowKst.getUTCMonth(),
        nowKst.getUTCDate(),
      ) - kstOffset;
      const targetUtc = todayKstMidnightUtc + h * 60 * 60 * 1000;

      // 현재 시각이 이미 지났으면 +1일
      const resultUtc = targetUtc <= nowUtc ? targetUtc + 86400000 : targetUtc;

      try {
        return new Date(resultUtc).toLocaleString('ko-KR', {
          timeZone: 'Asia/Seoul',
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit', second: '2-digit',
        });
      } catch {
        return '-';
      }
    },

    formatError(err) {
      // hotfix P1 #6: extractApiError 유틸 사용 — 딜러/ECOUNT 일관성 + i18n 키 매핑
      const apiErr = extractApiError(err);
      if (apiErr.messageKey && this.$te(apiErr.messageKey)) {
        return this.$t(apiErr.messageKey, apiErr.messageParams || {});
      }
      return apiErr.message || this.$t('ecountErpConsole_error_fetch_failed');
    },

    // formatDateTime은 mixin이 제공 (dealerEcountListMixin)

    cronToLabel(expr) {
      if (!expr) return '-';
      const presets = {
        '0 3 * * *': this.$t('ecountErpConsole_cron_daily', { time: '03:00' }),
        '0 4 * * *': this.$t('ecountErpConsole_cron_daily', { time: '04:00' }),
        '0 5 * * *': this.$t('ecountErpConsole_cron_daily', { time: '05:00' }),
        '0 6 * * *': this.$t('ecountErpConsole_cron_daily', { time: '06:00' }),
      };
      // hotfix P1: 프리셋 외 cron은 원본 표현식 그대로 표시 (사용자가 누락 못 알아채는 문제 fix)
      return presets[expr] || expr;
    },

    // ── 변경 이력 + 복원 메서드 (딜러 CronConsole 패턴 미러링) ─────────────

    async fetchAudits() {
      this.loadingAudits = true;
      try {
        const res = await ecountEtlApi.getConfigAudits(20);
        this.audits = res.data || [];
      } catch {
        this.audits = [];
      } finally {
        this.loadingAudits = false;
      }
    },

    /**
     * beforeSnapshot vs afterSnapshot — 2토글 비교만 (딜러 5필드와 달리 ecount는 2필드)
     * @param {Object} record - audit 항목
     * @returns {Array} [{ field, before, after }]
     */
    diffAuditChanges(record) {
      const b = record.beforeSnapshot || {};
      const a = record.afterSnapshot || {};
      const out = [];
      const fmt = (v) => (v === undefined || v === null ? '-' : String(v));
      if (b.gtinSyncEnabled !== a.gtinSyncEnabled) {
        out.push({ field: this.$t('ecountErpConsole_job_gtin'), before: fmt(b.gtinSyncEnabled), after: fmt(a.gtinSyncEnabled) });
      }
      if (b.serialLotSyncEnabled !== a.serialLotSyncEnabled) {
        out.push({ field: this.$t('ecountErpConsole_job_serial_lot'), before: fmt(b.serialLotSyncEnabled), after: fmt(a.serialLotSyncEnabled) });
      }
      return out;
    },

    isLatestAudit(record) {
      if (!this.audits.length) return false;
      return record._id === this.audits[0]._id;
    },

    onRestoreClick(record) {
      this.restoreModal = { open: true, target: record };
    },

    async onConfirmRestore() {
      const target = this.restoreModal.target;
      if (!target) { this.restoreModal.open = false; return; }
      this.restoreModal.open = false;
      this.restoring = target._id;
      this.errorMsg = '';
      this.successMsg = '';
      try {
        const res = await ecountEtlApi.restoreConfig(target._id, this.config ? this.config.version : 0);
        const updated = res.data;
        // no-op: 복원 대상이 현재 설정과 동일 → 변경 없음 안내
        if (updated && updated.noChange) {
          this.successMsg = this.$t('ecountErpConsole_audits_restore_nochange');
        } else {
          this.successMsg = this.$t('ecountErpConsole_audits_restore_ok', { v: updated.version });
        }
        // config 재조회 + 이력 갱신
        await this.fetchAll();
        await this.fetchAudits();
      } catch (err) {
        if (err?.response?.status === 409) {
          // version 충돌 — config self-refresh 후 안내
          const current = err.response.data?.currentConfig;
          if (current) { this.config = current; this.prefillCronEdit(); }
          this.errorMsg = this.$t('ecountErpConsole_conflict');
        } else {
          this.errorMsg = this.formatError(err);
        }
      } finally {
        this.restoring = null;
      }
    },

    // 사유 입력 모달 Promise 패턴 (딜러 askReason 동일)
    askReason() {
      return new Promise((resolve) => {
        this.reasonModal = { open: true, reason: '', resolve };
      });
    },

    onConfirmReason() {
      const r = this.reasonModal.resolve;
      const reason = this.reasonModal.reason || '';
      this.reasonModal.open = false;
      if (r) r(reason);
    },

    onCancelReason() {
      const r = this.reasonModal.resolve;
      this.reasonModal.open = false;
      if (r) r(null);
    },

    // ────────────────────────────────────────────────────────────────────────

    async patchToggle(field, value, reason) {
      const payload = { expectedVersion: this.config.version };
      payload[field] = value;
      if (reason) payload.reason = reason;
      try {
        const res = await ecountEtlApi.updateConfig(payload);
        this.config = res.data || res;
        this.successMsg = this.$t('ecountErpConsole_apply_ok', { v: this.config.version });
        // 토글 변경 후 이력 갱신
        await this.fetchAudits();
      } catch (err) {
        if (err?.response?.status === 409) {
          // 충돌 — 현재 config 자동 갱신
          const current = err.response.data?.currentConfig;
          if (current) this.config = current;
          this.errorMsg = this.$t('ecountErpConsole_conflict');
        } else {
          this.errorMsg = this.formatError(err);
        }
      }
    },

    async onToggleGtin(newValue) {
      const reason = await this.askReason();
      if (reason === null) return; // 사용자 취소
      this.saving = 'gtin';
      this.errorMsg = '';
      this.successMsg = '';
      try {
        await this.patchToggle('gtinSyncEnabled', newValue, reason);
      } finally {
        this.saving = null;
      }
    },

    async onToggleSerialLot(newValue) {
      const reason = await this.askReason();
      if (reason === null) return; // 사용자 취소
      this.saving = 'serial-lot';
      this.errorMsg = '';
      this.successMsg = '';
      try {
        await this.patchToggle('serialLotSyncEnabled', newValue, reason);
      } finally {
        this.saving = null;
      }
    },

    async onTriggerGtin() {
      this.triggering.gtin = true;
      this.errorMsg = '';
      this.successMsg = '';
      this.lastTrigger = { runId: null, status: null };
      try {
        const res = await ecountEtlApi.triggerGtinSync();
        this.lastTrigger = res.data || {};
        this.successMsg = this.$t('ecountErpConsole_trigger_ok', { type: this.$t('ecountErpConsole_job_gtin') });
      } catch (err) {
        this.errorMsg = this.formatError(err);
      } finally {
        this.triggering.gtin = false;
      }
    },

    async onTriggerSerialLot() {
      this.triggering.serialLot = true;
      this.errorMsg = '';
      this.successMsg = '';
      this.lastTrigger = { runId: null, status: null };
      try {
        const res = await ecountEtlApi.triggerSerialLotSync();
        this.lastTrigger = res.data || {};
        this.successMsg = this.$t('ecountErpConsole_trigger_ok', { type: this.$t('ecountErpConsole_job_serial_lot') });
      } catch (err) {
        this.errorMsg = this.formatError(err);
      } finally {
        this.triggering.serialLot = false;
      }
    },

    /**
     * Cron 주기 PATCH /ecount-etl/config/cron
     * 변경된 cron만 payload에 포함. 400/409/503 개별 처리.
     */
    async onApplyCron() {
      if (!this.config) return;
      this.errorMsg = '';
      this.successMsg = '';

      // 시(hour) 단위 입력 → "0 H * * *" cronExpr 구성, 변경된 항목만 payload에 포함
      const payload = { expectedVersion: this.config.version };
      let changed = false;

      const gtinH = this.cronEdit.gtinHour;
      const serialH = this.cronEdit.serialLotHour;

      if (gtinH != null && !isNaN(gtinH)) {
        const newGtin = `0 ${Math.floor(gtinH)} * * *`;
        if (newGtin !== (this.config.gtinCronExpr || '')) {
          payload.gtinCronExpr = newGtin;
          changed = true;
        }
      }
      if (serialH != null && !isNaN(serialH)) {
        const newSerial = `0 ${Math.floor(serialH)} * * *`;
        if (newSerial !== (this.config.serialLotCronExpr || '')) {
          payload.serialLotCronExpr = newSerial;
          changed = true;
        }
      }
      if (!changed) {
        this.errorMsg = this.$t('ecountErpConsole_cron_unchanged');
        return;
      }

      this.cronSaving = true;
      try {
        const res = await ecountEtlApi.patchCronConfig(payload);
        this.config = res.data || res;
        this.prefillCronEdit();
        this.successMsg = this.$t('ecountErpConsole_cron_apply_ok', { v: this.config.version });
      } catch (err) {
        const status = err?.response?.status;
        if (status === 400) {
          const msg = err.response.data?.message || '';
          this.errorMsg = this.$t('ecountErpConsole_cron_400', { msg });
        } else if (status === 409) {
          const current = err.response.data?.currentConfig;
          if (current) { this.config = current; this.prefillCronEdit(); }
          this.errorMsg = this.$t('ecountErpConsole_conflict');
        } else if (status === 503) {
          this.errorMsg = this.$t('ecountErpConsole_cron_503');
        } else {
          this.errorMsg = this.formatError(err);
        }
      } finally {
        this.cronSaving = false;
      }
    },

    /**
     * SessionPanel에서 emit('session-updated', data) 시 세션 ref 즉시 갱신 (REQ-ACT-001/002)
     * obsRefreshSession으로 서버 최신 상태 재조회 (Dashboard.vue 검증 패턴 답습)
     */
    onSessionUpdated() {
      // setup()의 obsRefreshSession — Options API에서 this로 접근 가능 (setup return 값)
      this.obsRefreshSession();
    },

    /**
     * #3 동기화 현황 섹션용 — obsHealth.lastGtinRun.at / lastSerialLotRun.at KST 포맷
     * BatchHealthPanel.vue의 formatDateTime 패턴 동일하게 적용
     * @param {string|null} iso
     * @returns {string}
     */
    formatObsDateTime(iso) {
      if (!iso) return '-';
      try {
        return new Date(iso).toLocaleString('ko-KR', {
          timeZone: 'Asia/Seoul',
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit', second: '2-digit',
        });
      } catch {
        return iso;
      }
    },

    /**
     * #3 동기화 현황 섹션용 — EtlRun status → a-tag 색상
     * @param {string} status
     * @returns {string}
     */
    runStatusColor(status) {
      switch (status) {
        case 'success': return 'green';
        case 'partial': return 'orange';
        case 'failed':  return 'red';
        case 'skipped': return 'default';
        default:        return 'default';
      }
    },
  },
};
</script>

<style scoped>
.page-header { display: flex; align-items: center; margin-bottom: 16px; }
.page-title { font-size: 20px; font-weight: 600; }
.master-row { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 8px 0; }
.master-label { display: flex; align-items: center; gap: 8px; }
.version-label { font-family: monospace; color: #999; font-size: 12px; }
.cron-row { display: flex; align-items: center; gap: 16px; padding: 10px 0; border-bottom: 1px solid #f5f5f5; }
.cron-row:last-of-type { border-bottom: none; }
.cron-edit-row { display: flex; align-items: flex-start; gap: 16px; padding: 10px 0; border-bottom: 1px solid #f5f5f5; }
.cron-edit-row:last-of-type { border-bottom: none; }
.cron-edit-body { flex: 1; display: flex; flex-direction: column; gap: 2px; }
.cron-current code { background: #fafafa; padding: 2px 8px; border-radius: 4px; font-family: monospace; }
.cron-label { min-width: 110px; padding-top: 2px; }
.cron-value { flex: 1; }
.cron-value code { background: #fafafa; padding: 2px 8px; border-radius: 4px; font-family: monospace; }
.cron-translated { color: #666; font-size: 12px; margin-left: 8px; }
.cron-next { font-size: 11px; color: #888; }
.hint { color: #888; font-size: 13px; }
.permission-warning { margin-top: 8px; }
.trigger-row { display: flex; flex-wrap: wrap; gap: 8px; }
.trigger-btn-wrap { display: inline-block; }
.trigger-session-hint { margin-top: 10px; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.trigger-result { margin-top: 12px; padding-top: 12px; border-top: 1px solid #f5f5f5; }
.cron-hour-row { display: flex; align-items: center; gap: 6px; margin-top: 6px; flex-wrap: wrap; }
.cron-hour-prefix { font-family: monospace; color: #888; font-size: 13px; }
.cron-hour-suffix { font-family: monospace; color: #888; font-size: 13px; }

/* 변경 이력 diff highlight (딜러 CronConsole 동일 패턴) */
.audit-diff-row { font-size: 12px; line-height: 1.6; }
.diff-field { font-weight: 600; color: #555; margin-right: 4px; }
.diff-before { color: #c62828; text-decoration: line-through; font-family: monospace; }
.diff-arrow { color: #999; margin: 0 6px; }
.diff-after { color: #2e7d32; font-family: monospace; }

@media (max-width: 767px) {
  .page-header { flex-wrap: wrap; gap: 8px; row-gap: 8px; }
  .cron-row { flex-direction: column; align-items: stretch; gap: 6px; }
  .cron-label, .cron-value, .cron-next { width: 100%; }
  .cron-edit-row { flex-direction: column; align-items: stretch; gap: 6px; }
  .trigger-row :deep(.ant-btn) { width: 100%; }
}
</style>
